import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import PlaybackStartingPreparing from './PlaybackStartingPreparing';
import PlaybackStartingError from './PlaybackStartingError';
import PLAYBACK_STARTING_STATE from './PlaybackStartingState';
import PlaybackStartingEntitlementLimit from './PlaybackStartingEntitlementLimit';
import { ImageComponent } from '../ImageComponent';
import { PlaybackContext } from '../../contexts/PlaybackContext';
import playbackStartingBackground from '../../../public/images/default-bg@1.5x.png';
import { breakpoints } from '../../styles/breakpoints';
import PlaybackStartingPin from './PlaybackStartingPin';

type PlaybackStartingContainerProps = {
	visible?: boolean;
};

const PlaybackStartingContainer = styled.div<PlaybackStartingContainerProps>`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: url(${playbackStartingBackground.src}) no-repeat;
	background-size: cover;
	background-color: #000;
	background-attachment: fixed;
	transition: opacity 0.2s ease-in;
	opacity: ${(props) => (props.visible ? 1 : 0)};
	pointer-events: ${(props) => (props.visible ? 'auto' : 'none')};
	z-index: 100; // same as the player itself
`;

const PlaybackStartingBackground = styled(ImageComponent)`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-size: cover;
	background-attachment: fixed;
	opacity: 0;
	transition: opacity 0.2s ease-in;

	&.loaded {
		opacity: 1;
	}

	display: none;

	@media ${breakpoints.sm} {
		display: block;
	}
`;

const PlaybackStartingContent = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 30px;
	padding: 0 5px 0;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	overflow: hidden;

	@media ${breakpoints.sm} {
		padding: 0 30px 0;
		bottom: 60px;
	}

	@media ${breakpoints.xl} {
		padding: 0 120px 0;
	}
`;

const { INITIALIZING, PREPARING, TRANSITIONING, PIN, RENTAL, ENTITLEMENT, ENTITLEMENT_LIMIT, ERROR } = PLAYBACK_STARTING_STATE;

export default function PlaybackStarting() {
	const { playlistItem, playbackStartingState } = useContext(PlaybackContext);

	const backgroundImage = useMemo(() => {
		if (!playlistItem) return null;

		return <PlaybackStartingBackground images={[playlistItem.getBackgroundImageUrl()]} />;
	}, [playlistItem]);

	const component = useMemo(() => {
		if (!playlistItem) return null;

		switch (playbackStartingState) {
			case INITIALIZING:
			case PREPARING:
			case TRANSITIONING:
			case RENTAL:
			case ENTITLEMENT:
				return <PlaybackStartingPreparing />;
			case PIN:
				return <PlaybackStartingPin />;
			case ENTITLEMENT_LIMIT:
				return <PlaybackStartingEntitlementLimit />;
			case ERROR:
				return <PlaybackStartingError />;
			default:
				return null;
		}
	}, [playbackStartingState, playlistItem]);

	const content = useMemo(() => {
		if (!component) return null;

		return <PlaybackStartingContent>{component}</PlaybackStartingContent>;
	}, [component]);

	return (
		<PlaybackStartingContainer visible={!!playbackStartingState}>
			{backgroundImage}
			{content}
		</PlaybackStartingContainer>
	);
}
